import { useState, useRef, useEffect, memo } from 'react';
import { PaginationProps } from './types';

function PaginationComponent({
  page,
  limit,
  totalCount,
  onPageChange,
  onLimitChange,
  limitOptions = [10, 30, 50, 100],
  labels = {},
}: PaginationProps) {
  const {
    rowsPerPage = 'Wyników na stronę',
    of = 'z',
    firstPage = 'Pierwsza strona',
    previousPage = 'Poprzednia strona',
    nextPage = 'Następna strona',
    lastPage = 'Ostatnia strona',
  } = labels;

  const totalPages = Math.ceil(totalCount / limit);
  const start = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalCount);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleSelectOption = (value: number) => {
    onLimitChange(value);
    setIsDropdownOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsDropdownOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        break;
      case 'ArrowDown': {
        e.preventDefault();
        const currentIndex = limitOptions.indexOf(limit);
        const nextIndex = Math.min(currentIndex + 1, limitOptions.length - 1);
        const nextValue = limitOptions[nextIndex];
        if (nextValue !== undefined) {
          onLimitChange(nextValue);
        }
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const currentIdx = limitOptions.indexOf(limit);
        const prevIndex = Math.max(currentIdx - 1, 0);
        const prevValue = limitOptions[prevIndex];
        if (prevValue !== undefined) {
          onLimitChange(prevValue);
        }
        break;
      }
      case 'Enter':
      case ' ':
        e.preventDefault();
        setIsDropdownOpen(false);
        break;
    }
  };

  return (
    <div
      className="w-full px-3 sm:px-4 md:px-5 flex flex-col sm:flex-row gap-4 sm:gap-6 md:gap-0 sm:justify-between sm:items-center"
      data-testid="pagination-container"
      role="navigation"
      aria-label="Pagination"
    >
      {/* Left: Results per page */}
      <div className="flex items-center gap-2 justify-center sm:justify-start">
        <div className="text-[#181A1B] text-sm font-normal leading-5 tracking-[0.25px]">
          {rowsPerPage}
        </div>
        <div className="relative w-[86px]" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            onKeyDown={handleKeyDown}
            className="w-full h-10 px-4 pr-10 rounded border border-[#878B8E] text-[#181A1B] text-sm font-normal leading-5 tracking-[0.25px] bg-white cursor-pointer hover:bg-[#E8EEF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] focus-visible:border-[#2A4455] flex items-center"
            data-testid="pagination-limit-button"
            aria-label={`${rowsPerPage}: ${limit}`}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
          >
            {limit}
          </button>
          <div className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center pointer-events-none">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 9L12 15L18 9"
                stroke="#181A1B"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Dropdown menu - opens upward */}
          {isDropdownOpen && (
            <div
              className="absolute bottom-full left-0 w-full mb-1 p-1 bg-[#FAFAFA] rounded shadow-lg z-50"
              data-testid="pagination-dropdown"
              role="listbox"
              aria-label={rowsPerPage}
            >
              {limitOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => handleSelectOption(option)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectOption(option);
                    }
                  }}
                  className={`w-full min-w-[48px] px-3 py-2 rounded text-[#181A1B] text-sm font-normal leading-5 tracking-[0.25px] transition-colors text-left hover:bg-[#E8EEF0] focus:bg-[#E8EEF0] focus:outline-none cursor-pointer ${
                    option === limit ? 'bg-[#E8EEF0]' : ''
                  }`}
                  data-testid={`pagination-option-${option}`}
                  role="option"
                  aria-selected={option === limit}
                  tabIndex={0}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: Range and navigation */}
      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 md:gap-8 justify-center sm:justify-end">
        <div
          className="text-center sm:text-right text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]"
          data-testid="pagination-info"
          aria-live="polite"
        >
          {start}-{end} {of} {totalCount}
        </div>
        <div className="flex items-center gap-1">
          {/* First page button */}
          <button
            onClick={() => onPageChange(1)}
            disabled={page === 1}
            className="p-2 min-w-[40px] min-h-[40px] rounded-lg border border-[#DCDDE0] disabled:opacity-50 hover:bg-[#E8EEF0] transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            data-testid="pagination-first"
            aria-label={firstPage}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M9.16667 14.1673L5 10.0007L9.16667 5.83398"
                stroke="#474A4C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.9999 14.1673L10.8333 10.0007L14.9999 5.83398"
                stroke="#474A4C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {/* Previous page button */}
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="p-2 min-w-[40px] min-h-[40px] rounded-lg border border-[#DCDDE0] disabled:opacity-50 hover:bg-[#E8EEF0] transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            data-testid="pagination-prev"
            aria-label={previousPage}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="#474A4C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {/* Next page button */}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
            className="p-2 min-w-[40px] min-h-[40px] rounded-lg border border-[#DCDDE0] disabled:opacity-50 hover:bg-[#E8EEF0] transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            data-testid="pagination-next"
            aria-label={nextPage}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7.5 15L12.5 10L7.5 5"
                stroke="#474A4C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {/* Last page button */}
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={page === totalPages}
            className="p-2 min-w-[40px] min-h-[40px] rounded-lg border border-[#DCDDE0] disabled:opacity-50 hover:bg-[#E8EEF0] transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            data-testid="pagination-last"
            aria-label={lastPage}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10.8333 14.1673L14.9999 10.0007L10.8333 5.83398"
                stroke="#474A4C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5 14.1673L9.16667 10.0007L5 5.83398"
                stroke="#474A4C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export const Pagination = memo(PaginationComponent);
